import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

export type AIProvider = 'openai' | 'gemini';

export type FunctionCallingMode = 'AUTO' | 'ANY' | 'NONE' | 'VALIDATED';

export interface OpenAIConfig {
  model?: string;
  temperature?: number;
}

export interface GeminiConfig {
  model?: string;
  temperature?: number;
  thinkingBudget?: number; // 0 to disable thinking for lower latency
  functionCallingMode?: FunctionCallingMode;
}

export interface AIRolloutConfig {
  userAllowlist?: string[];
  forceProviderForAllowlist?: AIProvider | null;
}

export interface AIOverride {
  provider?: AIProvider;
  model?: string;
}

// Main AI config document shape
export interface AIConfig {
  provider: AIProvider; // global default provider
  openai?: OpenAIConfig;
  gemini?: GeminiConfig;
  rollout?: AIRolloutConfig;
  overrides?: Record<string, AIOverride>; // per-user overrides by uid
  enabled?: boolean; // NEW: global kill switch
  maintenanceMessage?: string; // NEW: message shown to clients when disabled
  updatedAt?: Date;
}

// Defaults used when doc does not exist
const DEFAULT_CONFIG: AIConfig = {
  provider: 'openai',
  openai: {
    model: 'gpt-5-mini',
    temperature: 0.2,
  },
  gemini: {
    model: 'gemini-3.5-flash',
    temperature: 0.2,
    thinkingBudget: 0,
    functionCallingMode: 'AUTO',
  },
  rollout: {
    userAllowlist: [],
    forceProviderForAllowlist: null,
  },
  overrides: {},
};

// Casting helper for Firestore Timestamp -> Date
function toJsDate(v: unknown): Date | undefined {
  if (v && typeof v === 'object' && v instanceof Timestamp) {
    return v.toDate();
  }
  return undefined;
}

const CONFIG_PATH = { col: 'config', id: 'ai' } as const;

export async function getAIConfig(): Promise<AIConfig> {
  const ref = doc(db, CONFIG_PATH.col, CONFIG_PATH.id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // Return defaults if not present
    return { ...DEFAULT_CONFIG };
  }
  const data = snap.data() as any;

  const cfg: AIConfig = {
    provider: (data.provider as AIProvider) ?? DEFAULT_CONFIG.provider,
    openai: {
      model: typeof data?.openai?.model === 'string' ? data.openai.model : DEFAULT_CONFIG.openai?.model,
      temperature: typeof data?.openai?.temperature === 'number' ? data.openai.temperature : DEFAULT_CONFIG.openai?.temperature,
    },
    gemini: {
      model: typeof data?.gemini?.model === 'string' ? data.gemini.model : DEFAULT_CONFIG.gemini?.model,
      temperature: typeof data?.gemini?.temperature === 'number' ? data.gemini.temperature : DEFAULT_CONFIG.gemini?.temperature,
      thinkingBudget: typeof data?.gemini?.thinkingBudget === 'number' ? data.gemini.thinkingBudget : DEFAULT_CONFIG.gemini?.thinkingBudget,
      functionCallingMode: (data?.gemini?.functionCallingMode as FunctionCallingMode) ?? DEFAULT_CONFIG.gemini?.functionCallingMode,
    },
    rollout: {
      userAllowlist: Array.isArray(data?.rollout?.userAllowlist) ? data.rollout.userAllowlist.filter((x: any) => typeof x === 'string') : DEFAULT_CONFIG.rollout?.userAllowlist,
      forceProviderForAllowlist: (data?.rollout?.forceProviderForAllowlist as AIProvider) ?? DEFAULT_CONFIG.rollout?.forceProviderForAllowlist ?? null,
    },
    overrides: typeof data?.overrides === 'object' && data?.overrides ? data.overrides : {},
    enabled: (typeof data?.enabled === 'boolean' ? data.enabled : true),
    maintenanceMessage: (typeof data?.maintenanceMessage === 'string' ? data.maintenanceMessage : undefined),
    updatedAt: toJsDate(data?.updatedAt),
  };

  return cfg;
}

export async function updateAIConfig(partial: Partial<AIConfig>): Promise<AIConfig> {
  // Normalize some inputs
  const sanitized: any = { ...partial };

  // Coerce temperatures/thinkingBudget to numbers if strings are passed
  if (sanitized?.openai?.temperature !== undefined) {
    const n = Number(sanitized.openai.temperature);
    if (!Number.isNaN(n)) sanitized.openai.temperature = n;
  }
  if (sanitized?.gemini?.temperature !== undefined) {
    const n = Number(sanitized.gemini.temperature);
    if (!Number.isNaN(n)) sanitized.gemini.temperature = n;
  }
  if (sanitized?.gemini?.thinkingBudget !== undefined) {
    const n = Number(sanitized.gemini.thinkingBudget);
    if (!Number.isNaN(n)) sanitized.gemini.thinkingBudget = n;
  }
  if (sanitized?.rollout?.userAllowlist) {
    sanitized.rollout.userAllowlist = (sanitized.rollout.userAllowlist as string[])
      .map((s: string) => String(s || '').trim())
      .filter((s: string) => !!s);
  }

  const ref = doc(db, CONFIG_PATH.col, CONFIG_PATH.id);
  await setDoc(
    ref,
    {
      ...sanitized,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  // Return the merged current config
  return await getAIConfig();
}
